import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Avatar, AvatarGroup, Box, Button, Center, Circle, Drawer, Heading, HStack, Icon, IconButton, Input, Link, Portal, Presence, Separator, Spinner, Stack, Text, VStack, Wrap } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { advisingNoteUnlessRepeat } from '@src/components/sco/Advisors/cards/advisorCardText';
import { useBoardSeatCounts } from '@src/components/sco/Advisors/hooks/useBoardSeatCounts';
import { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { UserSessionReviews, hasSessionReviews } from '@src/components/sco/Advisors/reviews';
import { AdvisorFeaturedBadge } from '@src/components/sco/Advisors/shared/AdvisorFeaturedBadge';
import { AdvisorMarketplaceActionButton } from '@src/components/sco/Advisors/shared/AdvisorMarketplaceActionButton';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { advisorMarketplaceActions } from '@src/components/sco/Advisors/utils/advisorMarketplaceActions';
import { ChatContextProvider } from '@src/components/sco/Chat/ChatContext';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import { paywallPropsForMessagingTarget } from '@src/components/sco/Chat/directMessagePaywall';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { MaxIntroSentModal } from '@src/components/sco/Matching/UserMatchSendMessageButton';
import { memberProfileSeo } from '@src/components/sco/Members/UserProfile/memberProfileSeo';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { RichMarkdownInput } from '@src/components/sco/RichMarkdownInput';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { SSRGate } from '@src/components/sco/utilities/SSRGate';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { Query, ScoAdvisorProfile, ScoCompany, ScoThread, ScoUser, ScoUserExperience, ScoUserSkill, UpsertChatCommentInput, UpsertChatCommentPayload, UpsertDirectChatThreadInput, UpsertDirectChatThreadPayload } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { usePageSEO } from '@src/hooks/usePageSEO';
import { useSSRStatus } from '@src/hooks/useSSRStatus.ts';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import { MEMBER_PROFILE_STATUS, MemberProfileStatusProps } from '@src/lib/memberProfileStatus';
import { ensureValidHref } from '@src/lib/startups-url';
import moment from 'moment/moment';
import { parseAsString, useQueryState } from 'nuqs';
import { Fragment, ReactNode, useState } from 'react';
import { LuArrowRight, LuArrowUpRight, LuLockKeyhole, LuRefreshCw, LuTriangleAlert, LuX } from 'react-icons/lu';
const MEMBER_PROFILE_REVIEW_SUMMARY = gql`
  query MemberProfileReviewSummary($userId: String!) {
    userById(userId: $userId) {
      id
      userId
      sessionReviewSummary {
        averageScore
        count
      }
    }
  }
`;
type UserProfileProps = {
  userSlug: string;
  /**
   * When false, skip setting Members/Founders breadcrumbs so a parent shell
   * (e.g. Advisors profile) can own the trail. Still sets `activeContent.item`.
   * @default true
   */
  manageBreadcrumbs?: boolean;
  /** Tighter padding and no page wash — nested in Advisors hub. */
  embedded?: boolean;
  /**
   * Own the page SEO: canonical `/members/{slug}` (L5/L6), robots from `memberProfileStatus`
   * (`noindex` unless indexable), Person JSON-LD. Off where the profile is embedded in another page
   * (e.g. `/members/[slug]/book`, which is always `noindex`).
   * @default false
   */
  manageSeo?: boolean;
  /**
   * The profile sits beside the booking form (`/members/[slug]/book`): no intro composer and no
   * advisory CTAs, so the form is the page's one action (real-data walk #5).
   * @default false
   */
  hideActions?: boolean;
};
export const UserProfile = props => {
  const $ = _c(179);
  const {
    userSlug,
    manageBreadcrumbs: t0,
    embedded: t1,
    manageSeo: t2,
    hideActions: t3
  } = props;
  const manageBreadcrumbs = t0 === undefined ? true : t0;
  const embedded = t1 === undefined ? false : t1;
  const manageSeo = t2 === undefined ? false : t2;
  const hideActions = t3 === undefined ? false : t3;
  const activeContent = useActiveContentContext();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const {
    currentUser,
    currentUserHasContentPermission
  } = useCurrentUser();
  const advisorsPaths = useAdvisorsPaths();
  const isGuestViewer = !currentUser || !!currentUser.isGuest;
  const [, setPrompt] = useQueryState("prompt", parseAsString);
  let t4;
  if ($[0] !== currentUserHasContentPermission) {
    t4 = currentUserHasContentPermission("canAccessChat");
    $[0] = currentUserHasContentPermission;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const canAccessChat = t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = gql`
      query GroupMemberUser($userSlug: String) {
        groupMemberUser(userSlug: $userSlug) {
          id
          userId
          bannedAt
          firstName
          lastName
          fullName
          displayName
          bio
          shortBio
          title
          linkedin
          twitter
          lastActiveAt
          location
          pictureUrl
          profileVisibilityKey
          userSkills(order: [["experienceStrength", "desc"]]) {
            id
            userSkillId
            title
            description
            experienceNotes
            experienceStrength
          }
          userExperiences {
            id
            userExperienceId
            experienceTypeKey
            accomplishmentDescription
            accomplishmentTypeKeys
            createdAt
            endDate
            industryKeys
            organizationName
            positionTitle
            positionDescription
            startDate
          }
          slug
          isAdvisor
          isToolUser
          advisorProfile {
            id
            advisorProfileId
            statusKey
            isApproved
            isFeatured
            warmIntroKeys
            warmIntros {
              id
              key
              title
            }
            availabilityHoursPerMonth
            responsesCount
            foundersHelpedCount
            hourlyRate
            canAcceptSessions
            bioForAdvising
            advisingNote
            headline
            categories {
              id
              key
              title
            }
          }
          company {
            id
            companyId
            elevatorPitch
            logoUrl
            name
            url
            location
            locationCity
            locationState
            locationCountry {
              id
              title
            }
            annualRevenue {
              id
              title
            }
            fundingRaised {
              id
              title
            }
            fundingStage {
              id
              title
            }
            headcount {
              id
              title
            }
            industries {
              id
              title
            }
            stage {
              id
              title
            }
            yearFounded
          }
        }
      }
    `;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== userSlug) {
    t6 = {
      variables: {
        userSlug
      }
    };
    $[3] = userSlug;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const query = useQuery(t5, t6);
  const user = query.data?.groupMemberUser && !query.data.groupMemberUser?.bannedAt ? query.data.groupMemberUser : undefined;
  const visibilityKey = user?.profileVisibilityKey || "public";
  const viewerIsMemberOrAdmin = !!user && (currentUser?.userId === user.userId || !!currentUser?.isAdmin);
  let profileIsVisible;
  if ($[5] !== currentUser || $[6] !== user || $[7] !== viewerIsMemberOrAdmin || $[8] !== visibilityKey) {
    profileIsVisible = false;
    if (!user) {
      profileIsVisible = false;
    } else {
      if (visibilityKey === "public" || viewerIsMemberOrAdmin) {
        profileIsVisible = true;
      } else {
        if (visibilityKey === "private" && !!currentUser && !currentUser.isGuest && !!currentUser.isStartupsUnlimitedMember) {
          profileIsVisible = true;
        }
      }
    }
    $[5] = currentUser;
    $[6] = user;
    $[7] = viewerIsMemberOrAdmin;
    $[8] = visibilityKey;
    $[9] = profileIsVisible;
  } else {
    profileIsVisible = $[9];
  }
  const isNotFoundError = !!query.error && /NOT_FOUND|Record Not Found/.test(query.error.message);
  const showUnavailable = !query.loading && !profileIsVisible && (!!user || !query.error || isNotFoundError);
  useSSRStatus(showUnavailable ? 410 : 200);
  let t7;
  if ($[10] !== userSlug) {
    t7 = {
      slugOrId: userSlug
    };
    $[10] = userSlug;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = !manageSeo || !userSlug;
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = {
      variables: t7,
      skip: t8
    };
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const statusQuery = useQuery(MEMBER_PROFILE_STATUS, t9);
  const t10 = user?.userId;
  let t11;
  if ($[15] !== t10) {
    t11 = {
      userId: t10
    };
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = !profileIsVisible || !user?.userId;
  let t13;
  if ($[17] !== t11 || $[18] !== t12) {
    t13 = {
      variables: t11,
      skip: t12
    };
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const reviewSummaryQuery = useQuery(MEMBER_PROFILE_REVIEW_SUMMARY, t13);
  const reviewSummary = reviewSummaryQuery.data?.userById?.sessionReviewSummary ?? null;
  const seoReady = manageSeo && !(query.loading && !user) && !(statusQuery.loading && !statusQuery.data) && !(reviewSummaryQuery.loading && !reviewSummaryQuery.data);
  let t14;
  if ($[20] !== profileIsVisible || $[21] !== reviewSummary || $[22] !== seoReady || $[23] !== statusQuery || $[24] !== user) {
    t14 = seoReady ? memberProfileSeo({
      status: statusQuery.data?.memberProfileStatus ?? null,
      user: profileIsVisible && user ? {
        ...user,
        sessionReviewSummary: reviewSummary
      } : null
    }) : null;
    $[20] = profileIsVisible;
    $[21] = reviewSummary;
    $[22] = seoReady;
    $[23] = statusQuery;
    $[24] = user;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  usePageSEO(t14);
  const profileHref = `/members/${encodeURIComponent(user?.slug || user?.userId || userSlug)}`;
  const isApprovedAdvisor = profileIsVisible && !!user?.advisorProfile?.isApproved;
  let t15;
  if ($[26] !== activeContent || $[27] !== advisorsPaths || $[28] !== isApprovedAdvisor || $[29] !== manageBreadcrumbs || $[30] !== profileHref || $[31] !== profileIsVisible || $[32] !== user) {
    t15 = () => {
      activeContent.setItem(profileIsVisible ? user : null);
      if (manageBreadcrumbs) {
        const current = {
          title: !profileIsVisible ? "Private" : user?.fullName || "Loading...",
          href: profileHref
        };
        activeContent.setBreadcrumbs(isApprovedAdvisor ? [{
          title: "Advisors",
          href: advisorsPaths.base
        }, {
          title: "Find advisors",
          href: advisorsPaths.directory
        }, current] : [{
          title: "Members",
          href: "/members"
        }, {
          title: "Founders",
          href: "/founders"
        }, current]);
      }
      return () => {
        activeContent.setItem(null);
      };
    };
    $[26] = activeContent;
    $[27] = advisorsPaths;
    $[28] = isApprovedAdvisor;
    $[29] = manageBreadcrumbs;
    $[30] = profileHref;
    $[31] = profileIsVisible;
    $[32] = user;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = user?.userId;
  let t17;
  if ($[34] !== isApprovedAdvisor || $[35] !== manageBreadcrumbs || $[36] !== profileHref || $[37] !== profileIsVisible || $[38] !== t16) {
    t17 = [t16, profileIsVisible, manageBreadcrumbs, isApprovedAdvisor, profileHref];
    $[34] = isApprovedAdvisor;
    $[35] = manageBreadcrumbs;
    $[36] = profileHref;
    $[37] = profileIsVisible;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  useUniversalEffect(t15, t17);
  if (query.loading && !user) {
    let t18;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Center bg="bg.subtle" minHeight="full" padding={8} width="full"><Spinner /></Center>;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    return t18;
  }
  if (showUnavailable) {
    let t18;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Icon asChild={true} boxSize="3em" color="colorPalette.border"><LuLockKeyhole /></Icon>;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    let t19;
    if ($[42] !== getBreakpointValue) {
      t19 = getBreakpointValue(["xl", "2xl", "3xl"]);
      $[42] = getBreakpointValue;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== t19) {
      t20 = <Heading fontWeight="600" size={t19}>This profile is private.</Heading>;
      $[44] = t19;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    let t21;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <Button asChild={true} variant="outline"><NextLink href="/members">View Other Members<LuArrowRight /></NextLink></Button>;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    let t22;
    if ($[47] !== t20) {
      t22 = <VStack align="center" bg="bg.subtle" gap={8} height="full" justify="center" minHeight="full" padding={12} paddingBottom={24} width="full">{t18}{t20}{t21}</VStack>;
      $[47] = t20;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    return t22;
  }
  if (!user) {
    const t18 = embedded ? undefined : "bg.subtle";
    let t19;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <Icon asChild={true} boxSize="2.5em" color="fg.muted"><LuTriangleAlert /></Icon>;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    let t20;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <VStack gap={1}><Heading size="lg">We couldn’t load this profile</Heading><Text color="fg.muted" textStyle="sm">Check your connection and try again.</Text></VStack>;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    let t21;
    if ($[51] !== query) {
      t21 = () => void query.refetch().catch(_temp);
      $[51] = query;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    let t22;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <LuRefreshCw />;
      $[53] = t22;
    } else {
      t22 = $[53];
    }
    let t23;
    if ($[54] !== t21) {
      t23 = <Button size="sm" variant="outline" onClick={t21}>{t22}Try again</Button>;
      $[54] = t21;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] !== t18 || $[57] !== t23) {
      t24 = <VStack align="center" bg={t18} gap={4} justify="center" minHeight="full" padding={12} role="alert" textAlign="center" width="full">{t19}{t20}{t23}</VStack>;
      $[56] = t18;
      $[57] = t23;
      $[58] = t24;
    } else {
      t24 = $[58];
    }
    return t24;
  }
  const isSelf = user.userId === currentUser?.userId;
  const stackHeader = !!getBreakpointValue([true, true, false]);
  let t18;
  if ($[59] !== user.userSkills) {
    t18 = [...(user.userSkills || [])].sort(_temp2);
    $[59] = user.userSkills;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  const skills = t18;
  let t19;
  if ($[61] !== user.userExperiences) {
    t19 = sortExperiences(user.userExperiences);
    $[61] = user.userExperiences;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  const experiences = t19;
  let sections;
  if ($[63] !== experiences || $[64] !== isGuestViewer || $[65] !== isSelf || $[66] !== reviewSummary || $[67] !== setPrompt || $[68] !== skills || $[69] !== user.bio || $[70] !== user.company || $[71] !== user.firstName || $[72] !== user.userId) {
    sections = [];
    if (user.bio || isSelf) {
      const t20 = `About ${user.firstName}`;
      let t21;
      if ($[74] !== isSelf || $[75] !== setPrompt) {
        t21 = isSelf ? <Button size="xs" onClick={() => setPrompt("user-edit-profile")}>Edit</Button> : null;
        $[74] = isSelf;
        $[75] = setPrompt;
        $[76] = t21;
      } else {
        t21 = $[76];
      }
      let t22;
      if ($[77] !== user.bio) {
        t22 = user.bio ? <MarkdownRenderer boxProps={{
          lineHeight: "tall",
          maxWidth: "42rem"
        }} preset="ugc">{user.bio.replaceAll("\n\n", "\n<br>")}</MarkdownRenderer> : <EmptySectionHint>Add a bio so members know who you are.</EmptySectionHint>;
        $[77] = user.bio;
        $[78] = t22;
      } else {
        t22 = $[78];
      }
      let t23;
      if ($[79] !== t20 || $[80] !== t21 || $[81] !== t22) {
        t23 = {
          key: "about",
          label: t20,
          button: t21,
          body: t22
        };
        $[79] = t20;
        $[80] = t21;
        $[81] = t22;
        $[82] = t23;
      } else {
        t23 = $[82];
      }
      sections.push(t23);
    }
    if (hasSessionReviews(reviewSummary)) {
      let t20;
      if ($[83] !== user.userId) {
        t20 = {
          key: "reviews",
          label: "Reviews",
          body: <UserSessionReviews heading={null} userId={user.userId} />
        };
        $[83] = user.userId;
        $[84] = t20;
      } else {
        t20 = $[84];
      }
      sections.push(t20);
    }
    const companyLocation = companyLocationLabel(user.company, {
      allowFreeText: !isGuestViewer
    });
    if (isSelf || companyHasDisplayableContent(user.company, companyLocation)) {
      let t20;
      if ($[85] !== isSelf || $[86] !== setPrompt) {
        t20 = isSelf ? <Button size="xs" onClick={() => setPrompt("user-edit-startup")}>Edit</Button> : null;
        $[85] = isSelf;
        $[86] = setPrompt;
        $[87] = t20;
      } else {
        t20 = $[87];
      }
      const t21 = companyHasDisplayableContent(user.company, companyLocation) ? <CompanyLetterhead company={user.company} location={companyLocation} /> : <EmptySectionHint>Add your company to this profile.</EmptySectionHint>;
      let t22;
      if ($[88] !== t20 || $[89] !== t21) {
        t22 = {
          key: "company",
          label: "Company",
          button: t20,
          body: t21
        };
        $[88] = t20;
        $[89] = t21;
        $[90] = t22;
      } else {
        t22 = $[90];
      }
      sections.push(t22);
    }
    if (skills.length > 0 || isSelf) {
      let t20;
      if ($[91] !== isSelf || $[92] !== setPrompt) {
        t20 = isSelf ? <Button size="xs" onClick={() => setPrompt("user-edit-skills")}>Edit</Button> : null;
        $[91] = isSelf;
        $[92] = setPrompt;
        $[93] = t20;
      } else {
        t20 = $[93];
      }
      let t21;
      if ($[94] !== skills) {
        t21 = skills.length > 0 ? <SkillRankList skills={skills} /> : <EmptySectionHint>Add skills so members know how you can help.</EmptySectionHint>;
        $[94] = skills;
        $[95] = t21;
      } else {
        t21 = $[95];
      }
      let t22;
      if ($[96] !== t20 || $[97] !== t21) {
        t22 = {
          key: "skills",
          label: "Skills",
          button: t20,
          body: t21
        };
        $[96] = t20;
        $[97] = t21;
        $[98] = t22;
      } else {
        t22 = $[98];
      }
      sections.push(t22);
    }
    if (experiences.length > 0 || isSelf) {
      let t20;
      if ($[99] !== isSelf || $[100] !== setPrompt) {
        t20 = isSelf ? <Button size="xs" onClick={() => setPrompt("user-edit-experiences")}>Edit</Button> : null;
        $[99] = isSelf;
        $[100] = setPrompt;
        $[101] = t20;
      } else {
        t20 = $[101];
      }
      let t21;
      if ($[102] !== experiences) {
        t21 = experiences.length > 0 ? <ExperienceSplit experiences={experiences} /> : <EmptySectionHint>Add roles and accomplishments.</EmptySectionHint>;
        $[102] = experiences;
        $[103] = t21;
      } else {
        t21 = $[103];
      }
      let t22;
      if ($[104] !== t20 || $[105] !== t21) {
        t22 = {
          key: "experience",
          label: "Experience",
          button: t20,
          body: t21
        };
        $[104] = t20;
        $[105] = t21;
        $[106] = t22;
      } else {
        t22 = $[106];
      }
      sections.push(t22);
    }
    $[63] = experiences;
    $[64] = isGuestViewer;
    $[65] = isSelf;
    $[66] = reviewSummary;
    $[67] = setPrompt;
    $[68] = skills;
    $[69] = user.bio;
    $[70] = user.company;
    $[71] = user.firstName;
    $[72] = user.userId;
    $[73] = sections;
  } else {
    sections = $[73];
  }
  const t20 = embedded ? undefined : "bg.subtle";
  const t21 = embedded ? 8 : 20;
  let t22;
  if ($[107] !== embedded || $[108] !== getBreakpointValue) {
    t22 = embedded ? 0 : getBreakpointValue([8, 8, 10]);
    $[107] = embedded;
    $[108] = getBreakpointValue;
    $[109] = t22;
  } else {
    t22 = $[109];
  }
  let t23;
  if ($[110] !== embedded || $[111] !== getBreakpointValue) {
    t23 = embedded ? 0 : getBreakpointValue([4, 6, 8]);
    $[110] = embedded;
    $[111] = getBreakpointValue;
    $[112] = t23;
  } else {
    t23 = $[112];
  }
  let t24;
  if ($[113] !== getBreakpointValue) {
    t24 = getBreakpointValue([6, 8, 10]);
    $[113] = getBreakpointValue;
    $[114] = t24;
  } else {
    t24 = $[114];
  }
  const t25 = stackHeader ? "column" : "row";
  let t26;
  if ($[115] !== getBreakpointValue) {
    t26 = getBreakpointValue([4, 5, 8]);
    $[115] = getBreakpointValue;
    $[116] = t26;
  } else {
    t26 = $[116];
  }
  let t27;
  if ($[117] !== getBreakpointValue) {
    t27 = getBreakpointValue(["4.25em", "5em", "5.75em"]);
    $[117] = getBreakpointValue;
    $[118] = t27;
  } else {
    t27 = $[118];
  }
  let t28;
  if ($[119] !== t27) {
    t28 = {
      boxSize: t27,
      overflow: "hidden"
    };
    $[119] = t27;
    $[120] = t28;
  } else {
    t28 = $[120];
  }
  let t29;
  if ($[121] !== t28 || $[122] !== user) {
    t29 = <Box flexShrink={0}><UserAvatar disablePopover={true} disablePresence={true} noLink={true} user={user} avatarProps={t28} /></Box>;
    $[121] = t28;
    $[122] = user;
    $[123] = t29;
  } else {
    t29 = $[123];
  }
  let t30;
  if ($[124] !== getBreakpointValue) {
    t30 = getBreakpointValue(["3xl", "4xl", "5xl"]);
    $[124] = getBreakpointValue;
    $[125] = t30;
  } else {
    t30 = $[125];
  }
  let t31;
  if ($[126] !== t30 || $[127] !== user.fullName) {
    t31 = <Heading letterSpacing="tighter" size={t30}>{user.fullName}</Heading>;
    $[126] = t30;
    $[127] = user.fullName;
    $[128] = t31;
  } else {
    t31 = $[128];
  }
  const t32 = user.advisorProfile?.isFeatured;
  let t33;
  if ($[129] !== t32) {
    t33 = <AdvisorFeaturedBadge isFeatured={t32} size="md" />;
    $[129] = t32;
    $[130] = t33;
  } else {
    t33 = $[130];
  }
  let t34;
  if ($[131] !== t31 || $[132] !== t33) {
    t34 = <HStack align="center" columnGap={3} flexWrap="wrap" rowGap={1}>{t31}{t33}</HStack>;
    $[131] = t31;
    $[132] = t33;
    $[133] = t34;
  } else {
    t34 = $[133];
  }
  let t35;
  if ($[134] !== getBreakpointValue || $[135] !== user.shortBio) {
    t35 = user.shortBio && <Text color="fg/80" fontFamily="longform" fontWeight="medium" lineClamp={3} lineHeight="short" textStyle={getBreakpointValue(["lg", "xl", "2xl"])}>{user.shortBio}</Text>;
    $[134] = getBreakpointValue;
    $[135] = user.shortBio;
    $[136] = t35;
  } else {
    t35 = $[136];
  }
  let t36;
  if ($[137] !== t34 || $[138] !== t35) {
    t36 = <VStack align="stretch" gap={2}>{t34}{t35}</VStack>;
    $[137] = t34;
    $[138] = t35;
    $[139] = t36;
  } else {
    t36 = $[139];
  }
  let t37;
  if ($[140] !== canAccessChat || $[141] !== user) {
    t37 = <MetaLine canAccessChat={canAccessChat} user={user} />;
    $[140] = canAccessChat;
    $[141] = user;
    $[142] = t37;
  } else {
    t37 = $[142];
  }
  let t38;
  if ($[143] !== t36 || $[144] !== t37) {
    t38 = <VStack align="stretch" flex={1} flexGrow={1} gap={4} minWidth={0} width="full">{t36}{t37}</VStack>;
    $[143] = t36;
    $[144] = t37;
    $[145] = t38;
  } else {
    t38 = $[145];
  }
  let t39;
  if ($[146] !== t25 || $[147] !== t26 || $[148] !== t29 || $[149] !== t38) {
    t39 = <Stack align="flex-start" direction={t25} gap={t26}>{t29}{t38}</Stack>;
    $[146] = t25;
    $[147] = t26;
    $[148] = t29;
    $[149] = t38;
    $[150] = t39;
  } else {
    t39 = $[150];
  }
  let t40;
  if ($[151] !== hideActions || $[152] !== isGuestViewer || $[153] !== isSelf || $[154] !== user) {
    t40 = !isSelf && !isGuestViewer && !hideActions && <SSRGate disableSSR={true}><SendIntroForm user={user} /></SSRGate>;
    $[151] = hideActions;
    $[152] = isGuestViewer;
    $[153] = isSelf;
    $[154] = user;
    $[155] = t40;
  } else {
    t40 = $[155];
  }
  let t41;
  if ($[156] !== hideActions || $[157] !== isGuestViewer || $[158] !== user.advisorProfile || $[159] !== user.shortBio || $[160] !== user.userId) {
    t41 = <AdvisorStrip advisorProfile={user.advisorProfile} headline={user.shortBio} hideActions={hideActions} isGuestViewer={isGuestViewer} userId={user.userId} />;
    $[156] = hideActions;
    $[157] = isGuestViewer;
    $[158] = user.advisorProfile;
    $[159] = user.shortBio;
    $[160] = user.userId;
    $[161] = t41;
  } else {
    t41 = $[161];
  }
  let t42;
  if ($[162] !== getBreakpointValue || $[163] !== sections) {
    t42 = sections.length > 0 && <VStack align="stretch" gap={0} width="full">{sections.map((section, index) => <Box key={section.key} id={section.key}>{index > 0 && <Separator borderColor="border" marginY={getBreakpointValue([8, 10, 12])} />}<EditorialSection button={section.button} label={section.label}>{section.body}</EditorialSection></Box>)}</VStack>;
    $[162] = getBreakpointValue;
    $[163] = sections;
    $[164] = t42;
  } else {
    t42 = $[164];
  }
  let t43;
  if ($[165] !== t24 || $[166] !== t39 || $[167] !== t40 || $[168] !== t41 || $[169] !== t42) {
    t43 = <VStack align="stretch" gap={t24} maxWidth="container.md" mx="auto" width="full">{t39}{t40}{t41}{t42}</VStack>;
    $[165] = t24;
    $[166] = t39;
    $[167] = t40;
    $[168] = t41;
    $[169] = t42;
    $[170] = t43;
  } else {
    t43 = $[170];
  }
  let t44;
  if ($[171] !== t21 || $[172] !== t22 || $[173] !== t23 || $[174] !== t43) {
    t44 = <Box paddingBottom={t21} paddingTop={t22} paddingX={t23} width="full">{t43}</Box>;
    $[171] = t21;
    $[172] = t22;
    $[173] = t23;
    $[174] = t43;
    $[175] = t44;
  } else {
    t44 = $[175];
  }
  let t45;
  if ($[176] !== t20 || $[177] !== t44) {
    t45 = <VStack align="stretch" bg={t20} gap={0} minHeight="full" width="full">{t44}</VStack>;
    $[176] = t20;
    $[177] = t44;
    $[178] = t45;
  } else {
    t45 = $[178];
  }
  return t45;
};
const hasDisplayValue = (value?: string | number | null) => {
  if (value == null) {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  return normalized.length > 0 && normalized !== 'n/a' && normalized !== '-';
};
const displayExternalLabel = (value: string) => value.replace(/^https?:\/\//i, '').replace(/\/$/, '');

/**
 * Company location for the Location fact (walk #11). City / state / country from the structured
 * fields; the free-text `location` (which can hold a street number or postal code) is a fallback for
 * signed-in members only — guests may read city, state and country, never street or postal (L1).
 */
export const companyLocationLabel = (company: ScoCompany | null | undefined, {
  allowFreeText
}: {
  allowFreeText: boolean;
}): string | null => {
  if (!company) {
    return null;
  }
  const structured = [company.locationCity, company.locationState, company.locationCountry?.title].map(part => (part || '').trim()).filter(part => hasDisplayValue(part)).filter((part, index, parts) => parts.findIndex(other => other.toLowerCase() === part.toLowerCase()) === index);
  if (structured.length > 0) {
    return structured.join(', ');
  }
  return allowFreeText && hasDisplayValue(company.location) ? company.location.trim() : null;
};
const companyHasDisplayableContent = (company: ScoCompany | null | undefined, location: string | null) => {
  if (!company) {
    return false;
  }
  return Boolean(hasDisplayValue(company.name) || hasDisplayValue(company.elevatorPitch) || company.industries?.some(industry => hasDisplayValue(industry.title)) || !!location || hasDisplayValue(company.url) || hasDisplayValue(company.headcount?.title) || hasDisplayValue(company.stage?.title) || hasDisplayValue(company.fundingStage?.title) || hasDisplayValue(company.fundingRaised?.title) || hasDisplayValue(company.annualRevenue?.title) || hasDisplayValue(company.yearFounded));
};
const formatExperienceDates = (startDate?: string | null, endDate?: string | null) => {
  const start = startDate ? moment(startDate).format('YYYY') : null;
  if (!start && !endDate) {
    return '';
  }
  const end = endDate ? moment(endDate).format('YYYY') : 'Present';
  if (!start) {
    return end;
  }
  return `${start} — ${end}`;
};
const sortExperiences = (experiences?: ScoUserExperience[] | null) => [...(experiences || [])].sort((a, b) => {
  const aEnd = a.endDate ? moment(a.endDate).valueOf() : Number.POSITIVE_INFINITY;
  const bEnd = b.endDate ? moment(b.endDate).valueOf() : Number.POSITIVE_INFINITY;
  if (aEnd !== bEnd) {
    return bEnd - aEnd;
  }
  return moment(b.startDate || 0).valueOf() - moment(a.startDate || 0).valueOf();
});
const EmptySectionHint = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Box bg="colorPalette.subtle/40" borderColor="colorPalette.border/50" borderRadius="l2" borderStyle="dashed" borderWidth="1px" paddingX={4} paddingY={3}><Text color="fg.muted" textStyle="sm">{children}</Text></Box>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const MetaLine = t0 => {
  const $ = _c(25);
  const {
    canAccessChat,
    user
  } = t0;
  let parts;
  if ($[0] !== canAccessChat || $[1] !== user.lastActiveAt || $[2] !== user.linkedin || $[3] !== user.location || $[4] !== user.title || $[5] !== user.twitter) {
    parts = [];
    if (hasDisplayValue(user.title)) {
      parts.push(user.title);
    }
    if (hasDisplayValue(user.location)) {
      parts.push(user.location);
    }
    if (canAccessChat && user.lastActiveAt) {
      let t1;
      if ($[7] !== user.lastActiveAt) {
        t1 = moment(user.lastActiveAt).fromNow();
        $[7] = user.lastActiveAt;
        $[8] = t1;
      } else {
        t1 = $[8];
      }
      parts.push(`Active ${t1}`);
    }
    if (canAccessChat && hasDisplayValue(user.linkedin)) {
      let t1;
      if ($[9] !== user.linkedin) {
        t1 = ensureValidHref(user.linkedin);
        $[9] = user.linkedin;
        $[10] = t1;
      } else {
        t1 = $[10];
      }
      let t2;
      if ($[11] !== user.linkedin) {
        t2 = displayExternalLabel(user.linkedin);
        $[11] = user.linkedin;
        $[12] = t2;
      } else {
        t2 = $[12];
      }
      let t3;
      if ($[13] !== t1 || $[14] !== t2) {
        t3 = <Link color="fg.muted" href={t1} target="_blank" textDecoration="underline" textDecorationColor="border" textUnderlineOffset="3px">{t2}</Link>;
        $[13] = t1;
        $[14] = t2;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      parts.push(t3);
    }
    if (canAccessChat && hasDisplayValue(user.twitter)) {
      let t1;
      if ($[16] !== user.twitter) {
        t1 = ensureValidHref(user.twitter);
        $[16] = user.twitter;
        $[17] = t1;
      } else {
        t1 = $[17];
      }
      let t2;
      if ($[18] !== user.twitter) {
        t2 = displayExternalLabel(user.twitter);
        $[18] = user.twitter;
        $[19] = t2;
      } else {
        t2 = $[19];
      }
      let t3;
      if ($[20] !== t1 || $[21] !== t2) {
        t3 = <Link color="fg.muted" href={t1} target="_blank" textDecoration="underline" textDecorationColor="border" textUnderlineOffset="3px">{t2}</Link>;
        $[20] = t1;
        $[21] = t2;
        $[22] = t3;
      } else {
        t3 = $[22];
      }
      parts.push(t3);
    }
    $[0] = canAccessChat;
    $[1] = user.lastActiveAt;
    $[2] = user.linkedin;
    $[3] = user.location;
    $[4] = user.title;
    $[5] = user.twitter;
    $[6] = parts;
  } else {
    parts = $[6];
  }
  if (parts.length === 0) {
    return null;
  }
  let t1;
  if ($[23] !== parts) {
    t1 = <Text color="fg.muted" textStyle="sm">{parts.map(_temp3)}</Text>;
    $[23] = parts;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  return t1;
};
const EditorialSection = t0 => {
  const $ = _c(8);
  const {
    label,
    button,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Heading size="md">{label}</Heading>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== button || $[3] !== t1) {
    t2 = <HStack align="baseline" gap={3} justify="space-between">{t1}{button}</HStack>;
    $[2] = button;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <VStack align="stretch" gap={4}>{t2}{children}</VStack>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const AdvisorStrip = t0 => {
  const $ = _c(75);
  const {
    advisorProfile,
    headline,
    hideActions: t1,
    isGuestViewer: t2,
    userId
  } = t0;
  const hideActions = t1 === undefined ? false : t1;
  const isGuestViewer = t2 === undefined ? false : t2;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const {
    currentUser
  } = useCurrentUser();
  const paths = useAdvisorsPaths();
  const [inviteOpen, setInviteOpen] = useState(false);
  const {
    counts: seatCounts
  } = useBoardSeatCounts();
  const hasOpenSeats = seatCounts.drafts + seatCounts.searching > 0;
  const isSelf = userId === currentUser?.userId;
  let t3;
  if ($[0] !== getBreakpointValue) {
    t3 = getBreakpointValue([false, false, true]);
    $[0] = getBreakpointValue;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isRow = Boolean(t3);
  if (!advisorProfile?.isApproved) {
    return null;
  }
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== advisorProfile.advisingNote || $[3] !== advisorProfile.availabilityHoursPerMonth || $[4] !== advisorProfile.bioForAdvising || $[5] !== advisorProfile.canAcceptSessions || $[6] !== advisorProfile.categories || $[7] !== advisorProfile.foundersHelpedCount || $[8] !== advisorProfile.headline || $[9] !== advisorProfile.responsesCount || $[10] !== advisorProfile.warmIntros || $[11] !== getBreakpointValue || $[12] !== hasOpenSeats || $[13] !== headline || $[14] !== hideActions || $[15] !== isGuestViewer || $[16] !== isRow || $[17] !== isSelf || $[18] !== paths || $[19] !== userId) {
    const warmIntroLabels = advisorProfile.warmIntros?.map(_temp4).filter(Boolean) ?? [];
    const categoryLabels = advisorProfile.categories?.map(_temp5).filter(Boolean) ?? [];
    const actions = hideActions ? [] : advisorMarketplaceActions({
      canAcceptSessions: advisorProfile.canAcceptSessions,
      hasOpenSeats,
      isSelf
    }).filter(action => action.key !== "message" && action.key !== "profile" && (!isGuestViewer || action.key === "book"));
    const t17 = advisorProfile.bioForAdvising || advisorProfile.advisingNote;
    const t18 = headline || advisorProfile.headline;
    let t19;
    if ($[35] !== t17 || $[36] !== t18) {
      t19 = advisingNoteUnlessRepeat(t17, t18);
      $[35] = t17;
      $[36] = t18;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    const advisingNote = t19;
    const hours = advisorProfile.availabilityHoursPerMonth;
    const responses = advisorProfile.responsesCount ?? 0;
    const foundersHelped = advisorProfile.foundersHelpedCount ?? 0;
    const hasStats = hours != null || responses > 0 || foundersHelped > 0;
    const showSideColumn = hasStats || !hideActions && (isSelf || actions.length > 0);
    T1 = Box;
    t10 = "colorPalette.subtle";
    t11 = "colorPalette.border/30";
    t12 = "2xl";
    t13 = "1px";
    t14 = "green";
    t15 = "hidden";
    t16 = 0;
    T0 = HStack;
    t4 = "stretch";
    t5 = 0;
    const t20 = isRow ? "block" : "none";
    if ($[38] !== t20) {
      t6 = <Separator display={t20} orientation="vertical" />;
      $[38] = t20;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    t7 = "wrap";
    let t21;
    if ($[40] !== getBreakpointValue) {
      t21 = getBreakpointValue([5, 6, 7]);
      $[40] = getBreakpointValue;
      $[41] = t21;
    } else {
      t21 = $[41];
    }
    let t22;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <HStack gap={2.5}><Circle bg="colorPalette.solid" size="2" /><Text textStyle="labelXs">Open to advisory</Text></HStack>;
      $[42] = t22;
    } else {
      t22 = $[42];
    }
    const t23 = categoryLabels.length > 0 && <Heading letterSpacing="tight" lineHeight="short" size="xl">{categoryLabels.join("  \xB7  ")}</Heading>;
    let t24;
    if ($[43] !== advisingNote) {
      t24 = advisingNote && <Text color="colorPalette.fg/80" fontFamily="longform" textStyle="sm">{advisingNote}</Text>;
      $[43] = advisingNote;
      $[44] = t24;
    } else {
      t24 = $[44];
    }
    const t25 = warmIntroLabels.length > 0 && <VStack align="flex-start" gap={2}><Text color="colorPalette.fg/70" textStyle="sm">Makes warm intros to</Text><Wrap gap={1.5}>{warmIntroLabels.map(_temp6)}</Wrap></VStack>;
    if ($[45] !== t21 || $[46] !== t23 || $[47] !== t24 || $[48] !== t25) {
      t8 = <VStack align="flex-start" flex={1} gap={5} minWidth="16rem" padding={t21}>{t22}{t23}{t24}{t25}</VStack>;
      $[45] = t21;
      $[46] = t23;
      $[47] = t24;
      $[48] = t25;
      $[49] = t8;
    } else {
      t8 = $[49];
    }
    t9 = showSideColumn && <VStack align="stretch" bg="colorPalette.muted" flexShrink={0} gap={5} justify="space-between" minWidth={isRow ? "13.5rem" : "full"} padding={getBreakpointValue([5, 6])} width={isRow ? "13.5rem" : "full"}>{hasStats && <Wrap columnGap={8} rowGap={4}>{hours != null && <BandStat label="Hours / month" value={String(hours)} />}{responses > 0 && <BandStat label="Responses" value={String(responses)} />}{foundersHelped > 0 && <BandStat label="Startups helped" value={String(foundersHelped)} />}</Wrap>}{hideActions ? null : isSelf ? <Button asChild={true} size="sm" variant="outline"><NextLink href={paths.profileEdit}>Edit advisor profile</NextLink></Button> : actions.length > 0 && <VStack align="stretch" gap={2}>{actions.map(action_0 => <AdvisorMarketplaceActionButton key={action_0.key} action={action_0} bookingRef="profile" paths={paths} userId={userId} onInvite={() => setInviteOpen(true)} />)}</VStack>}</VStack>;
    $[2] = advisorProfile.advisingNote;
    $[3] = advisorProfile.availabilityHoursPerMonth;
    $[4] = advisorProfile.bioForAdvising;
    $[5] = advisorProfile.canAcceptSessions;
    $[6] = advisorProfile.categories;
    $[7] = advisorProfile.foundersHelpedCount;
    $[8] = advisorProfile.headline;
    $[9] = advisorProfile.responsesCount;
    $[10] = advisorProfile.warmIntros;
    $[11] = getBreakpointValue;
    $[12] = hasOpenSeats;
    $[13] = headline;
    $[14] = hideActions;
    $[15] = isGuestViewer;
    $[16] = isRow;
    $[17] = isSelf;
    $[18] = paths;
    $[19] = userId;
    $[20] = T0;
    $[21] = T1;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    T0 = $[20];
    T1 = $[21];
    t10 = $[22];
    t11 = $[23];
    t12 = $[24];
    t13 = $[25];
    t14 = $[26];
    t15 = $[27];
    t16 = $[28];
    t4 = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  let t17;
  if ($[50] !== T0 || $[51] !== t4 || $[52] !== t5 || $[53] !== t6 || $[54] !== t7 || $[55] !== t8 || $[56] !== t9) {
    t17 = <T0 align={t4} gap={t5} separator={t6} wrap={t7}>{t8}{t9}</T0>;
    $[50] = T0;
    $[51] = t4;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== T1 || $[59] !== t10 || $[60] !== t11 || $[61] !== t12 || $[62] !== t13 || $[63] !== t14 || $[64] !== t15 || $[65] !== t16 || $[66] !== t17) {
    t18 = <T1 bg={t10} borderColor={t11} borderRadius={t12} borderWidth={t13} colorPalette={t14} overflow={t15} padding={t16}>{t17}</T1>;
    $[58] = T1;
    $[59] = t10;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  let t19;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setInviteOpen(false);
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  let t20;
  if ($[69] !== inviteOpen || $[70] !== userId) {
    t20 = <InviteToBoardModal advisorUserId={userId} isOpen={inviteOpen} onClose={t19} />;
    $[69] = inviteOpen;
    $[70] = userId;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== t18 || $[73] !== t20) {
    t21 = <>{t18}{t20}</>;
    $[72] = t18;
    $[73] = t20;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  return t21;
};
const BandStat = t0 => {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <Text fontFamily="mono" fontWeight="semibold" textStyle="2xl">{value}</Text>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <Text color="colorPalette.fg/60" textStyle="label2xs">{label}</Text>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="flex-start" gap={0}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const CompanyLetterhead = t0 => {
  const $ = _c(38);
  const {
    company,
    location
  } = t0;
  let tickerItems;
  if ($[0] !== company.annualRevenue || $[1] !== company.fundingRaised || $[2] !== company.fundingStage || $[3] !== company.headcount || $[4] !== company.industries || $[5] !== company.stage || $[6] !== company.yearFounded || $[7] !== location) {
    const industryTitles = company.industries?.map(_temp7).filter(hasDisplayValue) || [];
    tickerItems = [];
    if (hasDisplayValue(company.stage?.title)) {
      let t1;
      if ($[9] !== company.stage.title) {
        t1 = {
          key: "stage",
          label: "Company Stage",
          value: company.stage.title
        };
        $[9] = company.stage.title;
        $[10] = t1;
      } else {
        t1 = $[10];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.fundingStage?.title)) {
      let t1;
      if ($[11] !== company.fundingStage.title) {
        t1 = {
          key: "fundingStage",
          label: "Funding Stage",
          value: company.fundingStage.title
        };
        $[11] = company.fundingStage.title;
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.fundingRaised?.title)) {
      let t1;
      if ($[13] !== company.fundingRaised.title) {
        t1 = {
          key: "fundingRaised",
          label: "Funding Raised",
          value: company.fundingRaised.title
        };
        $[13] = company.fundingRaised.title;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.headcount?.title)) {
      let t1;
      if ($[15] !== company.headcount.title) {
        t1 = {
          key: "headcount",
          label: "Team Size",
          value: company.headcount.title
        };
        $[15] = company.headcount.title;
        $[16] = t1;
      } else {
        t1 = $[16];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.annualRevenue?.title)) {
      let t1;
      if ($[17] !== company.annualRevenue.title) {
        t1 = {
          key: "annualRevenue",
          label: "Annual Revenue",
          value: company.annualRevenue.title
        };
        $[17] = company.annualRevenue.title;
        $[18] = t1;
      } else {
        t1 = $[18];
      }
      tickerItems.push(t1);
    }
    if (location) {
      let t1;
      if ($[19] !== location) {
        t1 = {
          key: "location",
          label: "Location",
          value: location
        };
        $[19] = location;
        $[20] = t1;
      } else {
        t1 = $[20];
      }
      tickerItems.push(t1);
    }
    if (hasDisplayValue(company.yearFounded)) {
      const t1 = String(company.yearFounded);
      let t2;
      if ($[21] !== t1) {
        t2 = {
          key: "yearFounded",
          label: "Founded",
          value: t1
        };
        $[21] = t1;
        $[22] = t2;
      } else {
        t2 = $[22];
      }
      tickerItems.push(t2);
    }
    if (industryTitles.length > 0) {
      tickerItems.push({
        key: "industries",
        label: industryTitles.length > 1 ? "Industries" : "Industry",
        value: industryTitles.join(", ")
      });
    }
    $[0] = company.annualRevenue;
    $[1] = company.fundingRaised;
    $[2] = company.fundingStage;
    $[3] = company.headcount;
    $[4] = company.industries;
    $[5] = company.stage;
    $[6] = company.yearFounded;
    $[7] = location;
    $[8] = tickerItems;
  } else {
    tickerItems = $[8];
  }
  let t1;
  if ($[23] !== company.name) {
    t1 = hasDisplayValue(company.name) && <Heading size="2xl">{company.name}</Heading>;
    $[23] = company.name;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  let t2;
  if ($[25] !== company.url) {
    t2 = hasDisplayValue(company.url) && <Link href={ensureValidHref(company.url)} target="_blank"><HStack color="fg.muted" gap={1} textStyle="sm"><Text>{displayExternalLabel(company.url)}</Text><LuArrowUpRight /></HStack></Link>;
    $[25] = company.url;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  let t3;
  if ($[27] !== t1 || $[28] !== t2) {
    t3 = <HStack align="baseline" flexWrap="wrap" gap={3}>{t1}{t2}</HStack>;
    $[27] = t1;
    $[28] = t2;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  let t4;
  if ($[30] !== company.elevatorPitch) {
    t4 = hasDisplayValue(company.elevatorPitch) && <MarkdownRenderer boxProps={{
      color: "fg.muted",
      lineHeight: "tall"
    }} preset="ugc">{company.elevatorPitch.replaceAll("\n\n", "\n<br>")}</MarkdownRenderer>;
    $[30] = company.elevatorPitch;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  let t5;
  if ($[32] !== tickerItems) {
    t5 = tickerItems.length > 0 && <Wrap gap={6} paddingTop={1}>{tickerItems.map(_temp8)}</Wrap>;
    $[32] = tickerItems;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  let t6;
  if ($[34] !== t3 || $[35] !== t4 || $[36] !== t5) {
    t6 = <VStack align="flex-start" gap={3}>{t3}{t4}{t5}</VStack>;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  return t6;
};
const SkillRankList = t0 => {
  const $ = _c(4);
  const {
    skills
  } = t0;
  let t1;
  if ($[0] !== skills) {
    const withNotes = skills.filter(_temp9);
    const t2 = skills.map(_temp10);
    let t3;
    if ($[2] !== t2) {
      t3 = <Wrap gap={2}>{t2}</Wrap>;
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t1 = <VStack align="stretch" gap={4} width="full">{t3}{withNotes.length > 0 && <VStack align="stretch" gap={3}>{withNotes.map(_temp11)}</VStack>}</VStack>;
    $[0] = skills;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const EXPERIENCE_GAP = 8;
const ExperienceSplit = t0 => {
  const $ = _c(6);
  const {
    experiences
  } = t0;
  let t1;
  if ($[0] !== experiences) {
    let t2;
    if ($[2] !== experiences.length) {
      t2 = (experience, index) => <HStack key={experience.id} align="flex-start" gap={5}><Text color="fg.muted" flexShrink={0} fontFamily="mono" minWidth="9rem" paddingTop="6px" textStyle="xs">{formatExperienceDates(experience.startDate, experience.endDate)}</Text><ExperienceRail isFirst={index === 0} isLast={index === experiences.length - 1} /><VStack align="flex-start" flex={1} gap={1} minWidth={0} paddingTop="1px">{hasDisplayValue(experience.experienceTypeKey) && <Text color="fg.subtle" textStyle="label2xs">{experience.experienceTypeKey}</Text>}{hasDisplayValue(experience.positionTitle) && <Text fontWeight="semibold" textStyle="lg">{experience.positionTitle}</Text>}{hasDisplayValue(experience.organizationName) && <Text color="fg.muted" textStyle="sm">{experience.organizationName}</Text>}{hasDisplayValue(experience.positionDescription) && <Text fontFamily="longform" marginTop={2} textStyle="md">{experience.positionDescription}</Text>}{hasDisplayValue(experience.accomplishmentDescription) && <Text color="fg.muted" fontFamily="longform" textStyle="sm">{experience.accomplishmentDescription}</Text>}</VStack></HStack>;
      $[2] = experiences.length;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = experiences.map(t2);
    $[0] = experiences;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <VStack align="stretch" gap={EXPERIENCE_GAP} width="full">{t1}</VStack>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

/** Hollow dot + 1px rail. Extends through the parent gap so entries read as one timeline. */
const ExperienceRail = t0 => {
  const $ = _c(6);
  const {
    isFirst,
    isLast
  } = t0;
  const gapVar = `var(--chakra-spacing-${EXPERIENCE_GAP})`;
  const t1 = isLast ? "calc(100% - 11px)" : `calc(-1 * ${gapVar})`;
  const t2 = isFirst ? "9px" : "0";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <Box aria-hidden={true} bg="border" bottom={t1} left="calc(50% - 0.5px)" position="absolute" top={t2} width="1px" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Circle bg="bg.subtle" borderColor="fg.muted" borderWidth="1px" boxSize="2.5" marginTop="6px" position="relative" zIndex={1} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <Box alignSelf="stretch" flexShrink={0} position="relative" width="3">{t3}{t4}</Box>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
const SendIntroForm = (props: {
  user: ScoUser;
}) => {
  const {
    currentUser
  } = useCurrentUser();
  const teamPaywall = paywallPropsForMessagingTarget(props.user, currentUser);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [chatThreadId, setChatThreadId] = useQueryState('chatThreadId', parseAsString);
  const [introChatMessage, setIntroChatMessage] = useState<string>('');
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const chatThreadQuery = useQuery(gql`
      query ($userSlug: String) {
        groupMemberUser(userSlug: $userSlug) {
          id
          directChatThreadWithCurrentUser {
            id
            threadId
            commentsCount
          }
        }
      }
    `, {
    // Guests have no direct threads; don't run a member-only read on public profiles.
    skip: !currentUser || currentUser.isGuest,
    variables: {
      userSlug: props.user.slug || props.user.userId
    }
  });
  const existingChatThread = chatThreadQuery.data?.groupMemberUser?.directChatThreadWithCurrentUser;
  const hasExistingConversation = (existingChatThread?.commentsCount ?? 0) > 0;
  const [upsertDirectChatThread] = useMutation<{
    upsertDirectChatThread: UpsertDirectChatThreadPayload;
  }>(gql`
    mutation upsertChatThread($input: UpsertDirectChatThreadInput!) {
      upsertDirectChatThread(input: $input) {
        thread {
          id
          threadId
        }
        viewer {
          id
          currentUser {
            id
            remainingMemberChatsCount
          }
        }
      }
    }
  `);
  const [upsertChatComment] = useMutation<UpsertChatCommentPayload>(gql`
    mutation ($input: UpsertChatCommentInput!) {
      upsertChatComment(input: $input) {
        comment {
          id
          commentId
        }
      }
    }
  `);
  const onSubmit = async () => {
    if (!introChatMessage) {
      setError('Enter a message first!');
      return;
    }
    setIsLoading(true);
    try {
      setError(null);
      const threadInput: UpsertDirectChatThreadInput = {
        memberUserIds: [props.user.userId, currentUser?.userId]
      };
      const threadResult = await upsertDirectChatThread({
        variables: {
          input: threadInput
        }
      });
      const commentInput: UpsertChatCommentInput = {
        threadId: threadResult.data.upsertDirectChatThread.thread.threadId,
        body: introChatMessage
      };
      await upsertChatComment({
        variables: {
          input: commentInput
        }
      });
      setChatThreadId(threadResult.data.upsertDirectChatThread.thread.threadId);
      chatThreadQuery.refetch();
    } catch (err) {
      setError(err);
    }
    setIsLoading(false);
  };
  if (isOpen && !existingChatThread?.threadId && currentUser?.remainingMemberChatsCount === 0) {
    return <MaxIntroSentModal open={isOpen} onClose={onClose} />;
  }
  return <>
      <PaywalledAction contentPermission={teamPaywall?.contentPermission ?? 'canAccessChat'} intent={teamPaywall ? undefined : 'member-message'} isUnlocked={teamPaywall?.isUnlocked} title={teamPaywall?.title ?? 'Upgrade to request an introduction.'} upgradeProductId={teamPaywall?.upgradeProductId} variant={'boxed'} upgradeBoxProps={{
      padding: '1em'
    }}>
        <>
          {hasExistingConversation ? <HStack bg={'colorPalette.container'} borderColor={'colorPalette.border/25'} borderRadius={'xl'} borderWidth={'1px'} gap={2} padding={1.5} paddingLeft={4} width={'full'}>
              <Text color={'fg.muted'} flex={1} textStyle={'sm'}>
                {`You already have a chat with ${props.user.firstName}.`}
              </Text>
              <Button size={'sm'} variant={'cta'} onClick={() => setChatThreadId(existingChatThread.threadId)}>
                Open Chat
              </Button>
            </HStack> : !isOpen ? <HStack bg={'colorPalette.container'} borderColor={'colorPalette.border/25'} borderRadius={'xl'} borderWidth={'1px'} gap={2} padding={1.5} paddingLeft={4} width={'full'}>
              <Input _placeholder={{
            color: 'fg.muted'
          }} autoFocus={false} bg={'transparent'} borderWidth={'0'} color={'fg'} flex={1} maxLength={500} name={'intro'} outline={'none'} padding={0} placeholder={`Write ${props.user.firstName} a note…`} shadow={'none'} textStyle={'sm'} onFocus={() => {
            onOpen();
          }} />
              <Button size={'sm'} variant={'cta'} onClick={() => onOpen()}>
                Send
              </Button>
            </HStack> : <Presence animationDuration={'moderate'} present={true} style={{
          width: '100%'
        }} animationName={{
          _open: 'slide-from-bottom, fade-in',
          _closed: 'slide-to-bottom, fade-out'
        }}>
              <VStack align={'stretch'} gap={2} marginY={2} width={'full'}>
                <Text textStyle={'badgeSm'}>Send an Introduction:</Text>
                <RichMarkdownInput autoFocus={true} placeholder={'Write your message...'} onChange={values => setIntroChatMessage(values.markdown)} />
                {error && <Text color={'fg.error'} textStyle={'sm'}>
                    {String(error)}
                  </Text>}
                <HStack justify={'flex-end'}>
                  <Text color={'fg.muted'} flexGrow={1} textStyle={'xs'}>
                    {'You can initiate '}
                    <strong>
                      {currentUser?.remainingMemberChatsCount}
                      {' more'}
                    </strong>{' '}
                    introductions today.
                  </Text>
                  <Box>
                    <Button disabled={isLoading} loading={isLoading} variant={'cta'} onClick={onSubmit}>
                      Send
                    </Button>
                  </Box>
                </HStack>
              </VStack>
            </Presence>}
        </>
      </PaywalledAction>
      <Portal>
        <Drawer.Root open={!!chatThreadId} placement={'end'} onOpenChange={e => {
        if (!e.open) {
          setChatThreadId(null);
        }
      }}>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content maxWidth={'480px'} width={'90%'}>
              <Drawer.CloseTrigger />
              <Drawer.Body bgColor={'colorPalette.container'} padding={0}>
                <ChatContextProvider activeThreadId={chatThreadId}>
                  <ChatThread
                // Tool-persona chats are tool-chat threads. The composer stays open here.
                bypassContentPermissions threadId={chatThreadId} renderCustomHeader={() => <UserProfileChatHeader threadId={chatThreadId} onClose={() => setChatThreadId(null)} />} />
                </ChatContextProvider>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Drawer.Root>
      </Portal>
    </>;
};
export const UserProfileChatHeader = props => {
  const $ = _c(65);
  const {
    currentUser
  } = useCurrentUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($threadId: String!) {
        forum {
          id
          thread(threadId: $threadId) {
            id
            title
            threadId
            threadTypeKey
            userMemberships {
              id
              items {
                id
                user {
                  id
                  userId
                  fullName
                  pictureUrl
                }
              }
            }
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.threadId) {
    t1 = {
      variables: {
        threadId: props.threadId
      }
    };
    $[1] = props.threadId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  const thread = query.data?.forum.thread;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== currentUser || $[4] !== props || $[5] !== query.error || $[6] !== thread) {
    const recipientUsers = thread ? thread.userMemberships.items.filter(membership => thread.userMemberships.items.length === 1 || membership.user.userId !== currentUser.userId).map(_temp12) : [];
    T2 = HStack;
    t8 = "center";
    t9 = "blur(10px)";
    t10 = "colorPalette.panel";
    t11 = "1px solid";
    t12 = "colorPalette.border/50";
    t13 = "3.5em";
    t14 = "flex-start";
    t15 = "1em";
    t16 = 1;
    if ($[27] !== query.error) {
      t17 = query.error && <Text>{"Error: "}{query.error.toString()}</Text>;
      $[27] = query.error;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    if ($[29] !== thread) {
      t18 = !thread && <Text>Loading...</Text>;
      $[29] = thread;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    T1 = HStack;
    t4 = "flex-start";
    t5 = "full";
    let t19;
    if ($[31] !== props) {
      t19 = () => {
        props.onClose();
      };
      $[31] = props;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <LuX />;
      $[33] = t20;
    } else {
      t20 = $[33];
    }
    if ($[34] !== t19) {
      t6 = <IconButton aria-label="Close Chat" borderRadius="md" size="sm" variant="outline" onClick={t19}>{t20}</IconButton>;
      $[34] = t19;
      $[35] = t6;
    } else {
      t6 = $[35];
    }
    t7 = <AvatarGroup>{recipientUsers.map(_temp13)}</AvatarGroup>;
    T0 = Text;
    t2 = "bold";
    t3 = recipientUsers.map(_temp14).join(", ");
    $[3] = currentUser;
    $[4] = props;
    $[5] = query.error;
    $[6] = thread;
    $[7] = T0;
    $[8] = T1;
    $[9] = T2;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
    $[14] = t14;
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
    $[18] = t18;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    T0 = $[7];
    T1 = $[8];
    T2 = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t13 = $[13];
    t14 = $[14];
    t15 = $[15];
    t16 = $[16];
    t17 = $[17];
    t18 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t19;
  if ($[36] !== T0 || $[37] !== t2 || $[38] !== t3) {
    t19 = <T0 fontWeight={t2}>{t3}</T0>;
    $[36] = T0;
    $[37] = t2;
    $[38] = t3;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== T1 || $[41] !== t19 || $[42] !== t4 || $[43] !== t5 || $[44] !== t6 || $[45] !== t7) {
    t20 = <T1 justify={t4} width={t5}>{t6}{t7}{t19}</T1>;
    $[40] = T1;
    $[41] = t19;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const t21 = `/inbox?threadId=${thread?.threadId}`;
  let t22;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <LuArrowUpRight />;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t21) {
    t23 = <div><Button asChild={true} size="sm" variant="outline"><NextLink href={t21} target="_blank">Open in Chat{t22}</NextLink></Button></div>;
    $[48] = t21;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== T2 || $[51] !== t10 || $[52] !== t11 || $[53] !== t12 || $[54] !== t13 || $[55] !== t14 || $[56] !== t15 || $[57] !== t16 || $[58] !== t17 || $[59] !== t18 || $[60] !== t20 || $[61] !== t23 || $[62] !== t8 || $[63] !== t9) {
    t24 = <T2 align={t8} backdropFilter={t9} bgColor={t10} borderBottom={t11} borderBottomColor={t12} height={t13} justify={t14} padding={t15} zIndex={t16}>{t17}{t18}{t20}{t23}</T2>;
    $[50] = T2;
    $[51] = t10;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
    $[60] = t20;
    $[61] = t23;
    $[62] = t8;
    $[63] = t9;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  return t24;
};
function _temp() {
  return null;
}
function _temp2(a, b) {
  return (b.experienceStrength ?? 0) - (a.experienceStrength ?? 0);
}
function _temp3(part, index) {
  return <Fragment key={index}>{index > 0 ? "  \xB7  " : null}{part}</Fragment>;
}
function _temp4(item) {
  return item.title;
}
function _temp5(item_0) {
  return item_0.title;
}
function _temp6(label) {
  return <SpecialtyTag key={label} colorPalette="cyan" label={label} />;
}
function _temp7(industry) {
  return industry.title;
}
function _temp8(item) {
  return <VStack key={item.key} align="flex-start" gap={1} minWidth="8rem"><Text color="fg.subtle" textStyle="labelXs">{item.label}</Text><Text fontWeight="medium" textStyle="sm">{item.value}</Text></VStack>;
}
function _temp9(skill) {
  return hasDisplayValue(skill.experienceNotes);
}
function _temp10(skill_0) {
  return <SpecialtyTag key={skill_0.id || skill_0.userSkillId} label={skill_0.title} />;
}
function _temp11(skill_1) {
  return <VStack key={skill_1.id || skill_1.userSkillId} align="stretch" gap={0.5}><Text fontWeight="medium" textStyle="sm">{skill_1.title}</Text><Text color="fg.muted" fontFamily="longform" textStyle="sm">{skill_1.experienceNotes}</Text></VStack>;
}
function _temp12(membership_0) {
  return membership_0.user;
}
function _temp13(user) {
  return <Avatar.Root key={user.userId} border="none" size="xs"><Avatar.Fallback /><Avatar.Image src={user.pictureUrl} /></Avatar.Root>;
}
function _temp14(user_0) {
  return user_0.fullName;
}